import { fetchAPI } from '@quickbit/backoffice-core-frontend';
import { snakeCase } from 'lodash';
import {
  Order,
  OrderSearchResponse,
  MerchantOrderFilters,
  Pagination,
  SortOptions,
} from './types';

export const searchOrders = async ({
  pagination,
  filters,
  sortOptions,
}: {
  pagination?: Pagination;
  filters?: MerchantOrderFilters;
  sortOptions?: SortOptions;
}) => {
  const convertedFilters = filters && {
    ...filters,
    createdFrom: filters.createdFrom ? filters.createdFrom.toISOString() : null,
    createdTo: filters.createdTo ? filters.createdTo.toISOString() : null,
  };
  const data = await fetchAPI<OrderSearchResponse>(`v1/orders:search-minimal`, {
    method: 'POST',
    body: {
      pagination,
      filters: convertedFilters,
      sortOptions,
      orderSum: false,
    },
  });

  return {
    ...data,
    orders: data.orders.map(({ completedAt, createdAt, ...rest }) => ({
      completedAt: completedAt ? new Date(completedAt) : null,
      createdAt: new Date(createdAt),
      ...rest,
    })),
  };
};

const convertOrderDates = (data: Order): Order => ({
  ...data,
  completedAt: data.completedAt ? new Date(data.completedAt) : null,
  createdAt: new Date(data.createdAt),
  purchasedAt: data.purchasedAt ? new Date(data.purchasedAt) : undefined,
  history: data.history.map((h) => ({
    ...h,
    createdAt: new Date(h.createdAt),
  })),
  errors: data.errors.map((e) => ({
    ...e,
    createdAt: new Date(e.createdAt),
  })),
  callbacks: data.callbacks.map((c) => ({
    ...c,
    requestBody: Object.keys(c.requestBody).reduce(
      (acc, key) => ({ ...acc, [snakeCase(key)]: c.requestBody[key] }),
      {}
    ),
    createdAt: new Date(c.createdAt),
  })),
  manualRefunds:
    data.manualRefunds && data.manualRefunds.length > 0
      ? data.manualRefunds.map((refund) => ({
          ...refund,
          createdAt: new Date(refund.createdAt),
        }))
      : null,
});

export const getOrder = async (orderId: string) => {
  const data = await fetchAPI<Order>(`v1/orders/${orderId}`);
  return convertOrderDates(data);
};

export const forceCancelOrder = async (orderId: string) => {
  const data = await fetchAPI<Order>(`v1/orders/${orderId}`, {
    method: 'DELETE',
  });
  return convertOrderDates(data);
};

export const retryProcessingPay = async (orderId: string) => {
  const data = await fetchAPI<{ success: boolean }>(
    `v1/retry-processing-pay/${orderId}`,
    {
      method: 'POST',
    }
  );
  return data.success;
};

export const retrySellCryptoForOrder = async (orderId: string) => {
  const data = await fetchAPI<{ success: boolean }>(
    `v1/retry-sell-crypto/${orderId}`,
    {
      method: 'POST',
    }
  );
  return data.success;
};

export const retryMoveToCryptoReceived = async (orderId: string) => {
  await fetchAPI(`v1/orders/${orderId}/retry-crypto-received`, {
    method: 'POST',
  });
  return true;
};

export const retryRefundForPayOrder = async (orderId: string) => {
  await fetchAPI(`v1/orders/${orderId}/retry-pay-refund`, {
    method: 'POST',
  });
  return true;
};

export const retryFinalInternalConfirmation = async ({
  orderId,
  payTransactionId,
  receiveOrderPreviewId,
}: {
  orderId: string;
  payTransactionId: string;
  receiveOrderPreviewId: string;
}) => {
  const data = await fetchAPI<{ success: boolean }>(
    `v1/retry-final-internal-confirmation/${orderId}`,
    {
      method: 'POST',
      body: {
        payTransactionId,
        receiveOrderPreviewId,
      },
    }
  );
  return data.success;
};

export const restartProcessingForOrder = async (orderId: string) => {
  const data = await fetchAPI<{ success: boolean }>(
    `v1/restart-processing/${orderId}`,
    {
      method: 'POST',
    }
  );
  return data.success;
};

export const createProcessingOrder = async (merchantId: string) => {
  await fetchAPI('v1/orders/create-processing', {
    method: 'POST',
    body: {
      merchantId,
    },
  });
};

export const resendCallback = async (orderId: string) => {
  return fetchAPI(`v1/orders/${orderId}/resend-callback`, {
    method: 'POST',
  });
};
